<script lang="ts" setup>
import TruckRouteShortInfo from "@/views/trucks/components/TruckRouteShortInfo.vue";
import LoadingIcon from "@/components/icons/LoadingIcon.vue";
import { computed, ref, watch } from "vue";
import { ActiveRouteStatuses, RouteStatus } from "@/constants/trip";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import type { Anchor } from "vuetify/framework";
import type { TruckRoute } from "@/api/trucks/dto/truck.dto";

const { t } = useI18n();
const { showAPIError } = useError();
const api = new API();

const model = defineModel<boolean>({ default: false });

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    openOnHover?: boolean;
    truckId: number | undefined;
    location?: Anchor;
    currentRouteId?: number;
  }>(),
  {
    disabled: false,
    location: "top",
    openOnHover: false,
    currentRouteId: undefined,
  },
);

const routes = ref<TruckRoute[]>([]);
const isLoadingRoutes = ref<boolean>(false);

const currentRoute = computed(() => {
  return routes.value.find((route) => ActiveRouteStatuses.includes(route.status));
});

const plannedRoutes = computed(() => {
  return routes.value.filter((route) => route.status === RouteStatus.planned);
});

const menuWidth = computed(() => {
  return routes.value.length ? 650 : 300;
});

async function getTruckRoutes() {
  if (!props.truckId) {
    return;
  }

  isLoadingRoutes.value = true;
  try {
    const response = await api.trucks.getTruckRoutes(props.truckId, [
      RouteStatus.inProgress,
      RouteStatus.notStarted,
      RouteStatus.planned,
    ]);

    if (response.success && response.data?.routes) {
      routes.value = response.data.routes;
    } else {
      showAPIError(response);
    }
  } catch (error) {
    console.error(error);
  } finally {
    isLoadingRoutes.value = false;
  }
}

watch(model, async (value) => {
  if (value) {
    await getTruckRoutes();
  } else {
    routes.value = [];
    isLoadingRoutes.value = false;
  }
});
</script>

<template>
  <v-menu
    v-model="model"
    :close-on-content-click="false"
    :disabled="disabled"
    :loading="isLoadingRoutes"
    :location="location"
    :open-on-hover="openOnHover"
    :width="menuWidth"
    offset="4"
  >
    <template #activator="{ props: menuProps }">
      <slot name="activator" v-bind="menuProps"></slot>
    </template>
    <v-card>
      <v-card-text>
        <slot name="header"></slot>

        <div v-if="isLoadingRoutes" class="d-flex align-center justify-center mt-2">
          <loading-icon :size="40" color="primary" />
        </div>

        <template v-else>
          <div v-if="currentRoute">
            <div class="text-grey-darken-3 font-weight-500">{{ t("trucks.current-route") }}:</div>
            <truck-route-short-info
              :current-route-id="currentRouteId"
              :index="1"
              :route="currentRoute"
              :trip-id="1"
              class="mt-2"
            >
              <template #actions="routeProps">
                <slot name="actions" v-bind="routeProps"></slot>
              </template>
            </truck-route-short-info>
          </div>

          <div v-if="plannedRoutes.length" class="mt-3">
            <div class="text-grey-darken-3 font-weight-500">{{ t("trucks.planned-routes") }}:</div>

            <truck-route-short-info
              v-for="(route, index) of plannedRoutes"
              :key="route.id"
              :current-route-id="currentRouteId"
              :index="index + 1"
              :route="route"
              :trip-id="1"
              class="mt-2"
            >
              <template #actions="routeProps">
                <slot name="actions" v-bind="routeProps"></slot>
              </template>
            </truck-route-short-info>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-menu>
</template>
